import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import {
  anonymousContext,
  createClient,
  readFlagOverride,
  writeBootstrap,
  type LDClient,
  type LDFlagSet,
} from "@/lib/launchdarkly/client";
import { buildContext } from "@/lib/launchdarkly/context";
import { LAUNCHDARKLY_ENABLED } from "@/config";

export class FeatureFlagStore {
  flags: LDFlagSet = {};
  isReady = false;

  private rootStore: RootStore;
  private client: LDClient | null = null;
  private disposeReaction: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  initialize(): void {
    if (this.client) return;

    // No live LD client (local dev, and the hermetic CI/e2e stack, which builds
    // the frontend without a LaunchDarkly client id). Don't spin up a client, but
    // honor an explicit `ld:flags-override` localStorage entry so tests and local
    // dev can force flags deterministically without a real LD environment — the
    // seam e2e specs write via `page.addInitScript`. This is intentionally NOT
    // `ld:bootstrap` (which the live SDK writes on every user): keeping them
    // separate means a prod build accidentally shipped with LD unconfigured falls
    // back to all-off rather than resurfacing a user's stale cached flags. With no
    // override present, every flag resolves to its fallback (off) — equivalent to
    // the previous no-op.
    if (!LAUNCHDARKLY_ENABLED) {
      const override = readFlagOverride();
      runInAction(() => {
        this.flags = override ?? {};
        this.isReady = true;
      });
      return;
    }

    const client = createClient(anonymousContext());
    if (!client) return;
    this.client = client;

    client.on("ready", () => {
      const next = client.allFlags();
      runInAction(() => {
        this.flags = next;
        this.isReady = true;
      });
      writeBootstrap(next);
    });

    client.on("change", () => {
      const next = client.allFlags();
      runInAction(() => {
        this.flags = next;
      });
      writeBootstrap(next);
    });

    this.disposeReaction = reaction(
      () => ({
        user: this.rootStore.user.currentUser,
        workspace: this.rootStore.workspace.currentWorkspace,
      }),
      ({ user, workspace }) => {
        if (!user || !this.client) return;
        this.client.identify(buildContext(user, workspace));
      },
      // fireImmediately so an already-loaded user is identified at wiring time.
      // `initialize()` runs from `entry.client.tsx` before hydration, so in the
      // app this reaction is usually wired before auth-guard's clientLoader has
      // populated currentUser and fires on that change. Kept for the callers
      // that wire it late — a store initialized after its user loaded (tests)
      // would otherwise never see a user *change*, so identify() never fires
      // and the person stays the anonymous LD context.
      { fireImmediately: true },
    );
  }

  async flush(): Promise<void> {
    await this.client?.flush();
  }

  isEnabled(key: string, fallback = false): boolean {
    const value = this.flags[key];
    return typeof value === "boolean" ? value : fallback;
  }

  variation<T>(key: string, fallback: T): T {
    const value = this.flags[key];
    return value === undefined ? fallback : (value as T);
  }
}
